import { useApolloClient, useMutation, useQuery, useReactiveVar } from '@apollo/client';

import { ICreateEntityParams, IEntitiesResults } from './entity.types';
import { IEntity, Identifiable } from '../../../lib/types';
import { useCallback, useMemo } from 'react';
import { updateEntityCache } from './entity.utils';
import {
  CREATE_ENTITY,
  DELETE_ENTITY,
  GET_ENTITIES,
  GET_ENTITY,
  GET_ENTITY_OVERVIEWS,
  RESTORE_ENTITY,
  UPDATE_ENTITY
} from './entity.queries';
import { IArchivableProps, IUpdateCompanyPermissionsVariantParams } from '../../../lib/query.types';
import { activeCompanyLoadingVar } from '../companies/company.service';
import { IPermanent } from '../request-group/request-group.types';
import { GET_CLIENT_INVITES } from '../client-invite/client-invite.queries';
import { GET_REQUEST, LIST_REQUESTS_AS_STAFF } from '../request/request.queries';
import { GET_REQUEST_GROUP, GET_REQUEST_GROUPS } from '../request-group/request-group.queries';

export const useEntity = (variables: Identifiable) => {
  const { data, loading, error, called, refetch, networkStatus, client, observable, previousData } = useQuery<{ entity: IEntity }>(GET_ENTITY, { fetchPolicy: 'cache-and-network', variables });

  const entity = useMemo(() => data?.entity, [data?.entity]);

  return useMemo(() => ({
    loading,
    error,
    called,
    refetch,
    networkStatus,
    client,
    observable,
    previousData,
    entity
  }), [loading, error, called, refetch, networkStatus, client, observable, previousData, entity]);
};

export const useEntities = () => {
  const activeCompanyLoading = useReactiveVar(activeCompanyLoadingVar);
  const { data, loading, error, called, refetch, networkStatus, client, observable, previousData } = useQuery<IEntitiesResults, IArchivableProps>(GET_ENTITIES, {
    fetchPolicy: 'cache-and-network',
    variables: { includeArchived: true }
  });

  const entities = useMemo(() => data?.entities, [data?.entities]);
  const combinedLoading = useMemo(() => loading || activeCompanyLoading, [loading, activeCompanyLoading]);

  return useMemo(() => ({
    loading: combinedLoading,
    error,
    called,
    refetch,
    networkStatus,
    client,
    observable,
    previousData,
    entities
  }), [combinedLoading, error, called, refetch, networkStatus, client, observable, previousData, entities]);
};

export const useEntityOverviews = () => {
  const activeCompanyLoading = useReactiveVar(activeCompanyLoadingVar);
  const { data, loading, error, called, refetch, networkStatus, client, observable, previousData } = useQuery<IEntitiesResults, IArchivableProps>(GET_ENTITY_OVERVIEWS, {
    fetchPolicy: 'cache-and-network',
    variables: { includeArchived: true }
  });

  const entities = useMemo(() => data?.entities, [data?.entities]);
  const combinedLoading = useMemo(() => loading || activeCompanyLoading, [loading, activeCompanyLoading]);

  return useMemo(() => ({
    loading: combinedLoading,
    error,
    called,
    refetch,
    networkStatus,
    client,
    observable,
    previousData,
    entities
  }), [combinedLoading, error, called, refetch, networkStatus, client, observable, previousData, entities]);
};

export const useCreateEntity = () => {
  const [mutation, rest] = useMutation<{ createEntity: IEntity }, ICreateEntityParams>(CREATE_ENTITY, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data) updateEntityCache({ cache, entities: [data.createEntity] });
    }
  });

  const createEntity = useCallback((variables: ICreateEntityParams) => mutation({ variables }), [mutation]);

  return { createEntity, ...rest };
};

export const useDeleteEntity = () => {
  const client = useApolloClient();

  const [mutation, rest] = useMutation<{ deleteEntity: IEntity }, Identifiable & IPermanent>(DELETE_ENTITY, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }, { variables }) => {
      if (data) {
        updateEntityCache({ cache, entities: [data.deleteEntity], remove: variables?.permanent });

        if (variables?.permanent)
          client.refetchQueries({
            include: [
              GET_CLIENT_INVITES,
              GET_ENTITIES,
              GET_ENTITY,
              GET_REQUEST,
              LIST_REQUESTS_AS_STAFF,
              GET_REQUEST_GROUP,
              GET_REQUEST_GROUPS
            ]
          });
      }
    }
  });

  const deleteEntity = useCallback((variables: Identifiable & IPermanent) => mutation({ variables }), [mutation]);

  return { deleteEntity, ...rest };
};

export const useRestoreEntity = () => {
  const [mutation, rest] = useMutation<{ restoreEntity: IEntity }, Identifiable>(RESTORE_ENTITY, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data) updateEntityCache({ cache, entities: [data.restoreEntity] });
    }
  });

  const restoreEntity = useCallback((variables: Identifiable) => mutation({ variables }), [mutation]);

  return { restoreEntity, ...rest };
};

export const useUpdateEntity = () => {
  const [mutation, rest] = useMutation<{ updateEntity: IEntity }, IUpdateCompanyPermissionsVariantParams>(
    UPDATE_ENTITY,
    {
      context: { serializationKey: 'MUTATION', tracked: true },
      update: (cache, { data }) => {
        if (data) updateEntityCache({ cache, entities: [data.updateEntity] });
      }
    }
  );

  const updateEntity = useCallback(
    (variables: IUpdateCompanyPermissionsVariantParams) => mutation({ variables }),
    [mutation]
  );

  return { updateEntity, ...rest };
};
